import { FieldArray, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { useBreakpoint } from '@app/hooks';
import { PAGE_BREAKPOINTS } from '@app/constants/commonConstants';
import AddItemRows from './AddItemRows';
import { AlloyButton, PopupTrigger } from 'cat-ecommerce-alloy';
import ProductFormLabels from './ProductFormLabels';
import CopyAndPastePopup from './CopyAndPastePopup';
import FileUpload from './FileUpload';
import ExceptionFeedbackBySelector from '../Exception/ExceptionFeedbackBySelector';
import styles from './ProductForm.module.scss';

const ProductForm = ({
  actionButtons,
  clearButtonClassName,
  clearButtonSize = 'dynamic',
  defaultRows = 2,
  errorInfo,
  formContentClassName,
  handleOpenImportChild,
  initialItems,
  maxRows = 180,
  onClearAll,
  productFormTitle,
  shoppingCartMaxItems,
  showDivider,
  titleClassName,
  withCopyPastePopup,
  withFileUpload,
  ...props
}) => {
  const { t } = useTranslation();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const { values, setFormikState } = useFormikContext();

  const getFileUploadField = () => (
    <>
      {values.showNotes && <hr className="w-100" />}
      <FileUpload
        errorInfo={errorInfo}
        handleOpenImportChild={handleOpenImportChild}
        shoppingCartMaxItems={shoppingCartMaxItems}
      />
    </>
  );

  const getClearButton = () => {
    const valuesEdited = !!values.items?.find(item => item.itemNumber !== ''); //had to do this instead of dirty prop because of savedlist required behavior to disable buttons
    const handleClearClick = () => {
      const rowArray = Array.from(
        document.getElementsByClassName('product-form-row')
      );
      rowArray.forEach((row, index) => {
        if (index > 1) {
          row.classList.remove(styles['product__form-row-dynamic-add']);
          row.classList.add(styles['product__form-row-deleted']);
        }
      });
      setTimeout(async () => {
        setFormikState(s => ({
          ...s,
          values: { ...values, items: initialItems },
          errors: {},
          touched: {}
        }));
      }, 600);
      if (onClearAll) {
        onClearAll();
      }
    };
    return (
      <AlloyButton
        buttonType="ghost"
        className={cx(
          {
            'ps-0': isMD
          },
          clearButtonClassName
        )}
        disabled={!valuesEdited}
        // need to do mouseDown instead of click to handle button click before form onBlur validation
        onMouseDown={handleClearClick}
        size={clearButtonSize}
        type="button"
      >
        {t('CPG_CLEAR_ALL')}
      </AlloyButton>
    );
  };

  return (
    <>
      {!!productFormTitle && (
        <h2
          className={cx('h3 u-color--ExtraDarkGray mb-1 pb-1', titleClassName)}
        >
          {productFormTitle}
        </h2>
      )}
      {!!errorInfo?.domain && !!errorInfo.path && (
        <ExceptionFeedbackBySelector
          className="mb-2"
          selector={state => state.errors[errorInfo.domain]?.[errorInfo.path]}
          showReferenceId
        />
      )}
      <div
        className={cx('d-flex', {
          'flex-row': !withFileUpload || isMD,
          'flex-column': (withFileUpload && !isMD) || values.showNotes
        })}
      >
        <div
          className={cx('col-12 px-0', {
            'pe-3': !values.showNotes && withFileUpload && isMD,
            'mb-1': values.showNotes && isMD,
            [`col-lg-7 ${styles['product-form__fields-wrapper']}`]:
              !values.showNotes && withFileUpload
          })}
        >
          <ProductFormLabels showNotes={values.showNotes && isMD} />
          <div className={cx(formContentClassName)}>
            <FieldArray name="items">
              {fieldArrayProps => (
                <AddItemRows
                  {...fieldArrayProps}
                  defaultRows={defaultRows}
                  errorInfo={errorInfo}
                  maxRows={maxRows}
                  withCopyPastePopup={withCopyPastePopup}
                  {...props}
                />
              )}
            </FieldArray>
          </div>
          {withCopyPastePopup && (
            <CopyAndPastePopup trigger={PopupTrigger.HOVER} />
          )}
          <div
            className={cx(
              'd-flex',
              {
                'align-items-start pt-1': clearButtonSize === 'small',
                'justify-content-between': isMD,
                'flex-column': !isMD
              },
              formContentClassName
            )}
          >
            {isMD && getClearButton()}
            {actionButtons}
            {!isMD && getClearButton()}
          </div>
          {withFileUpload && !isMD && getFileUploadField()}
        </div>
        {withFileUpload && isMD && getFileUploadField()}
      </div>
      {((!values.showNotes && isMD) || showDivider) && (
        <hr className="w-100 pb-2" />
      )}
    </>
  );
};

ProductForm.propTypes = {
  actionButtons: PropTypes.node,
  clearButtonClassName: PropTypes.string,
  clearButtonSize: PropTypes.string,
  defaultRows: PropTypes.number,
  errorInfo: PropTypes.shape({
    domain: PropTypes.string,
    path: PropTypes.string
  }),
  formContentClassName: PropTypes.string,
  handleOpenImportChild: PropTypes.func,
  initialItems: PropTypes.arrayOf(
    PropTypes.shape({
      quantity: PropTypes.string,
      itemNumber: PropTypes.string,
      assetId: PropTypes.string,
      lineItemNote: PropTypes.string,
      customerPartNumber: PropTypes.string,
      customerItemNumber: PropTypes.string,
      id: PropTypes.string
    })
  ),
  maxRows: PropTypes.number,
  onClearAll: PropTypes.func,
  productFormTitle: PropTypes.string,
  titleClassName: PropTypes.string,
  showDivider: PropTypes.bool,
  withCopyPastePopup: PropTypes.bool,
  withFileUpload: PropTypes.bool,
  shoppingCartMaxItems: PropTypes.number,
  defaultRowValues: PropTypes.shape({
    quantity: PropTypes.string,
    itemNumber: PropTypes.string,
    assetId: PropTypes.string,
    lineItemNote: PropTypes.string,
    customerPartNumber: PropTypes.string,
    customerItemNumber: PropTypes.string
  })
};

export default ProductForm;
